import { apiUrl, responseError } from "./api";
import type {
  MatterDraft,
  SessionSummary,
  TranscriptDetail,
  TranscriptRow,
  TranscriptSummary
} from "./ui-types";

const proceedingType: Record<MatterDraft["proceeding"], string> = {
  "Deposition / Examination": "deposition-examination",
  "Investigation / Interview": "investigation-interview",
  Conference: "conference",
  Hearing: "hearing"
};

const proceedingLabel: Record<string, MatterDraft["proceeding"]> = {
  "deposition-examination": "Deposition / Examination",
  "investigation-interview": "Investigation / Interview",
  conference: "Conference",
  hearing: "Hearing"
};

const languageCode = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "english") return "en";
  if (normalized === "spanish") return "es";
  if (normalized === "french") return "fr";
  if (normalized === "mandarin" || normalized === "chinese") return "zh";
  return normalized.slice(0, 16) || "und";
};

const languageLabel = (value: string) =>
  ({ en: "English", es: "Spanish", fr: "French", zh: "Mandarin" })[value] ?? value;

const participantRole = (value: string) => {
  const role = value.toLowerCase();
  if (role.includes("witness")) return "witness";
  if (role.includes("interpreter")) return "interpreter";
  if (role.includes("judge")) return "judge";
  if (role.includes("registrar")) return "registrar";
  if (role.includes("investigator")) return "investigator";
  if (role.includes("interviewer")) return "interviewer";
  if (role.includes("interviewee")) return "interviewee";
  if (role.includes("examining") || role.includes("examiner")) return "examiner";
  if (role.includes("counsel") || role.includes("solicitor")) return "counsel";
  return "other";
};

export function matterPayload(matter: MatterDraft) {
  return {
    matter: {
      name: matter.title,
      reference: matter.reference,
      proceedingType: proceedingType[matter.proceeding],
      jurisdictionPackId: matter.jurisdictionPack,
      venue: matter.venue,
      scheduledAt: new Date(`${matter.date || new Date().toISOString().slice(0, 10)}T12:00:00Z`).toISOString(),
      timezone: matter.timezone,
      expectedLanguages: matter.languages.map(languageCode),
      glossary: matter.glossary,
      confidentialityNote: matter.authorityReference,
      legalHold: false
    },
    participants: matter.participants.map((participant) => ({
      displayName: participant.name,
      role: participantRole(participant.role),
      preferredSpeakerLabel: participant.name,
      expectedLanguages: matter.languages.map(languageCode)
    }))
  };
}

function detailFromServer(body: Record<string, any>): TranscriptDetail {
  const matter = body.matter as Record<string, any>;
  return {
    id: body.id,
    status: body.status,
    revision: body.revision,
    updatedAt: body.updatedAt,
    finalisedAt: body.finalisedAt ?? null,
    matter: {
      title: matter.name ?? "",
      reference: matter.reference ?? "",
      proceeding: proceedingLabel[matter.proceedingType] ?? "Conference",
      jurisdictionPack: matter.jurisdictionPackId ?? "GLOBAL-BASE",
      venue: matter.venue ?? "",
      date: String(matter.scheduledAt ?? new Date().toISOString()).slice(0, 10),
      timezone: matter.timezone ?? "UTC",
      languages: (matter.expectedLanguages ?? ["en"]).map(languageLabel),
      participants: (body.participants ?? []).map((participant: Record<string, any>) => ({
        name: participant.displayName,
        role: participant.role
      })),
      glossary: matter.glossary ?? [],
      authorityReference: matter.confidentialityNote ?? "",
      authority: {
        userAttestation: false,
        participantNotice: false,
        participantConsent: false,
        confidentialityReviewed: false,
        courtApproval: false
      }
    },
    sessions: body.sessions as SessionSummary[],
    finalisation: body.finalisation ?? null
  };
}

async function json(response: Response, fallback: string) {
  if (!response.ok) throw await responseError(response, fallback);
  return (await response.json()) as Record<string, any>;
}

export async function listTranscripts(
  status: "open" | "finalised" | "all",
  query = ""
): Promise<TranscriptSummary[]> {
  const params = new URLSearchParams({ status });
  if (query.trim()) params.set("q", query.trim());
  const body = await json(
    await fetch(apiUrl(`/api/matters?${params.toString()}`), { credentials: "same-origin" }),
    "Transcripts could not be loaded."
  );
  return body.transcripts as TranscriptSummary[];
}

export async function createTranscript(matter: MatterDraft): Promise<TranscriptDetail> {
  const body = await json(
    await fetch(apiUrl("/api/matters"), {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(matterPayload(matter))
    }),
    "A new transcript could not be created."
  );
  return detailFromServer(body);
}

export async function loadTranscript(id: string): Promise<TranscriptDetail> {
  return detailFromServer(
    await json(
      await fetch(apiUrl(`/api/matters/${encodeURIComponent(id)}`), {
        credentials: "same-origin"
      }),
      "Transcript details could not be loaded."
    )
  );
}

export async function deleteTranscript(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/matters/${encodeURIComponent(id)}`), {
    method: "DELETE",
    credentials: "same-origin"
  });
  if (!response.ok) {
    throw await responseError(response, "Transcript could not be deleted.");
  }
}

export async function saveTranscript(
  detail: TranscriptDetail,
  matter: MatterDraft
): Promise<TranscriptDetail> {
  const body = await json(
    await fetch(apiUrl(`/api/matters/${encodeURIComponent(detail.id)}`), {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expectedRevision: detail.revision, ...matterPayload(matter) })
    }),
    "Transcript details could not be saved."
  );
  return detailFromServer(body);
}

type CompositeNode = {
  id: string;
  kind?: TranscriptRow["kind"];
  line: number;
  text: string;
  timeRange: { startMs: number; endMs: number };
  speaker: { speakerId: string | null; label: string; confidence: number | null } | null;
  confidence: {
    wordConfidence: number | null;
    speakerConfidence: number | null;
    alignmentConfidence: number | null;
    mismatch: boolean;
    overlap: boolean;
  };
  sessionId: string;
  sessionOrdinal: number;
  sessionLabel: string | null;
  sessionStartedAt: string;
  sourceTranscriptId: string;
  sourceVersionId: string;
  reviewResolved?: boolean;
};

const percent = (value: number | null) => Math.round((value ?? 0) * 100);

export async function loadCompositeTranscript(
  id: string,
  sessionOrdinal?: number
): Promise<{ rows: TranscriptRow[]; sessions: SessionSummary[] }> {
  const suffix = sessionOrdinal ? `?session=${sessionOrdinal}` : "";
  const body = await json(
    await fetch(apiUrl(`/api/matters/${encodeURIComponent(id)}/transcript${suffix}`), {
      credentials: "same-origin"
    }),
    "Transcript text could not be loaded."
  );
  const sessions = body.sessions as SessionSummary[];
  const sessionsById = new Map(sessions.map((session) => [session.id, session]));
  return {
    sessions,
    rows: (body.rows as CompositeNode[]).map((node) => ({
      id: node.id,
      kind: node.kind ?? "speech",
      line: node.line,
      startMs: node.timeRange.startMs,
      endMs: node.timeRange.endMs,
      speakerId: node.speaker?.speakerId ?? "unknown",
      speaker: node.speaker?.label ?? "Unknown speaker",
      role: node.speaker?.label ?? "UNKNOWN SPEAKER",
      baseline: node.text,
      text: node.text,
      wordConfidence: percent(node.confidence.wordConfidence),
      speakerConfidence: percent(node.confidence.speakerConfidence),
      alignmentConfidence: percent(node.confidence.alignmentConfidence),
      reviewScore: Math.min(
        percent(node.confidence.wordConfidence),
        percent(node.confidence.speakerConfidence)
      ),
      reasons: [
        ...(percent(node.confidence.wordConfidence) < 65 ? (["low-confidence"] as const) : []),
        ...((node.kind ?? "speech") === "speech" &&
        percent(node.confidence.speakerConfidence) < 65
          ? (["uncertain-speaker"] as const)
          : []),
        ...(node.confidence.mismatch ? (["provider-disagreement"] as const) : []),
        ...(node.confidence.overlap ? (["overlap"] as const) : [])
      ],
      sessionId: node.sessionId,
      sessionOrdinal: node.sessionOrdinal,
      sessionLabel: node.sessionLabel,
      sessionStartedAt: node.sessionStartedAt,
      sourceTranscriptId: node.sourceTranscriptId,
      sourceVersionId: node.sourceVersionId,
      reviewResolved: node.reviewResolved,
      sourceAudio: sessionsById.get(node.sessionId)?.sourceAudio
    }))
  };
}

export async function finaliseTranscript(id: string) {
  return json(
    await fetch(apiUrl(`/api/matters/${encodeURIComponent(id)}/finalise`), {
      method: "POST",
      credentials: "same-origin"
    }),
    "Transcript could not be finalised."
  );
}
